import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from './firebase';

export default function DeliverPage() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const qy = query(collection(db, 'bookings'), where('bookingId', '==', bookingId));
      const snap = await getDocs(qy);
      if (!snap.empty) {
        const d = snap.docs[0];
        setBooking({ id: d.id, ...d.data() });
      }
    } catch (e) {
      setError('Could not load this delivery. Check your connection.');
    }
    setLoading(false);
  };

useEffect(() => { load(); }, [bookingId]);

  const markDelivered = async () => {
    if (!booking) return;
    setUpdating(true);
    try {
      await updateDoc(doc(db, 'bookings', booking.id), { orderStatus: 'in_trial' });
      await load();
    } catch (e) {
      setError('Could not update. Please try again.');
    }
    setUpdating(false);
  };

  const wrap = { minHeight: '100vh', background: '#f5f5f5', fontFamily: 'Arial', paddingBottom: '40px' };
  const header = (
    <div style={{ background: '#111', color: 'white', padding: '20px 22px', textAlign: 'center' }}>
      <div style={{ fontFamily: 'Georgia, serif', fontSize: '20px', letterSpacing: '5px' }}>FIVETRY</div>
      <div style={{ fontSize: '10px', letterSpacing: '2px', color: '#b89968', marginTop: '2px' }}>DELIVERY · RETURNS</div>
    </div>
  );

  if (loading) return <div style={wrap}>{header}<p style={{ textAlign: 'center', color: '#666', padding: '50px 20px' }}>Loading…</p></div>;

  if (!booking) return (
    <div style={wrap}>{header}
      <div style={{ textAlign: 'center', padding: '50px 24px' }}>
        <p style={{ color: '#666' }}>No delivery found for <strong>{bookingId}</strong>.</p>
        <p style={{ color: '#999', fontSize: '13px' }}>Please re-check the link with FIVETRY.</p>
      </div>
    </div>
  );

  const status = booking.orderStatus || 'booked';
  const delivered = ['in_trial', 'paid', 'completed'].includes(status);
  const decided = Array.isArray(booking.purchasedItems);
  const keptKey = (x) => (x.name || '') + '|' + (x.size || '');
  const keptSet = decided ? new Set(booking.purchasedItems.map(keptKey)) : null;
  const items = Array.isArray(booking.selectedItems) ? booking.selectedItems : [];

  return (
    <div style={wrap}>{header}
      <div style={{ maxWidth: '480px', margin: '0 auto', padding: '22px 18px' }}>
        <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
          <div style={{ fontWeight: '700', color: '#16a34a', fontSize: '16px', marginBottom: '12px' }}>{booking.bookingId}</div>
          <p style={{ margin: '4px 0', fontSize: '15px' }}><strong>{booking.customerName}</strong></p>
          <p style={{ margin: '4px 0', fontSize: '14px', color: '#444' }}>{booking.customerAddress}</p>
          <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>{booking.deliveryDay} · {booking.timeSlot}</p>
          <a href={'tel:' + booking.customerPhone} style={{ display: 'inline-block', margin: '8px 0 16px', fontSize: '15px', color: '#1d4ed8', textDecoration: 'none', fontWeight: '600' }}>📞 {booking.customerPhone}</a>

          <div style={{ fontSize: '10px', letterSpacing: '1px', color: '#9c7d4f', fontWeight: '700', marginBottom: '10px' }}>
            {decided ? 'CHECK RETURNS' : 'ITEMS TO DELIVER'} · {items.length} ITEM{items.length !== 1 ? 'S' : ''}
          </div>

          {items.map((it, i) => {
            if (typeof it === 'string') {
              return <div key={i} style={{ fontSize: '13px', color: '#333', padding: '6px 0' }}>{it}</div>;
            }
            const kept = keptSet ? keptSet.has(keptKey(it)) : null;
            return (
              <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'center', background: '#fafaf7', border: '1px solid #eeeae3', borderRadius: '10px', padding: '10px', marginBottom: '8px' }}>
                {it.image && <img src={it.image} alt={it.name} style={{ width: '58px', height: '74px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0, background: '#eee' }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', color: '#1a1a1a', fontWeight: '600', lineHeight: '1.3' }}>{it.name}</div>
                  {it.size && it.size !== 'Not specified' && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>Size: <strong style={{ color: '#9c7d4f' }}>{it.size}</strong></div>
                  )}
                  {kept !== null && (
                    <span style={{ display: 'inline-block', marginTop: '6px', fontSize: '10px', fontWeight: '700', letterSpacing: '0.5px', padding: '3px 9px', borderRadius: '999px', background: kept ? '#dcfce7' : '#fef3c7', color: kept ? '#16a34a' : '#b45309' }}>
                      {kept ? 'KEEPING ✓' : 'COLLECT BACK ↩'}
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {decided && (
            <p style={{ fontSize: '11px', color: '#888', margin: '12px 0 0', lineHeight: '1.5' }}>
              Collect every item marked <strong style={{ color: '#b45309' }}>COLLECT BACK ↩</strong>, and check each piece matches its photo before accepting the return.
            </p>
          )}

          {error && <p style={{ color: '#dc2626', fontSize: '13px', margin: '12px 0 0' }}>{error}</p>}

          <div style={{ marginTop: '18px' }}>
            {!delivered ? (
              <>
                <button onClick={markDelivered} disabled={updating} style={{ width: '100%', background: updating ? '#888' : '#16a34a', color: 'white', border: 'none', padding: '16px', borderRadius: '12px', fontSize: '15px', fontWeight: '700', letterSpacing: '1px', cursor: updating ? 'not-allowed' : 'pointer' }}>
                  {updating ? 'UPDATING…' : 'MARK DELIVERED · START TRIAL'}
                </button>
                <p style={{ fontSize: '11px', color: '#999', textAlign: 'center', marginTop: '10px', lineHeight: '1.5' }}>
                  Tap once the customer has received their items. This starts their trial.
                </p>
              </>
            ) : (
              <div style={{ textAlign: 'center', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '14px' }}>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#16a34a' }}>
                  {(status === 'paid' || status === 'completed') ? 'Customer has paid ✓' : 'Trial started ✓'}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {decided ? 'Collect the items marked above.' : 'Customer is trying the items now.'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}